import { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Spin } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { useGetForecastingForFysQuery } from '@/api/forecasting';
import { fyCodesInRange } from './utils/fyCalendar';
import type { DashboardFilters, KpiLens } from './forecasting.types';
import {
  filterRows,
  formatFyLabel,
  formatCurrentQuarterLabel,
  getDefaultThreeMonthFilters,
  metricFromLens,
  uniqueFyOptions
} from './utils/forecastingAggregations';
import {
  buildCampaignDetailRows,
  computeQuarterTotals,
  filterRowsByMonth,
  groupDrillComparison,
  groupDrillSummary,
  groupByMonthGrouped
} from './utils/threeMonthAggregations';
import { uncountedStatuses } from './utils/filterHelpers';
import {
  canDrillDownLevel,
  canDrillUpLevel,
  DRILL_LEVELS,
  drillSelectionLabel,
  filterRowsByDrillSelection,
  getDrillLevelConfig,
  type DrillSelection
} from './utils/drilldownTable';
import { FilterSidebar } from './components/FilterSidebar';
import { ThreeMonthKpiScorecards } from './components/ThreeMonthKpiScorecards';
import { MonthlyGroupedChart } from './components/MonthlyGroupedChart';
import { BuyingPointSummaryTable } from './components/BuyingPointSummaryTable';
import { BuyingPointComparisonChart } from './components/BuyingPointComparisonChart';
import { CampaignDetailTable } from './components/CampaignDetailTable';
import type { BuyingPointSummaryRow } from './threeMonth.types';
import {
  ActiveLensHint,
  CollapsibleLayout,
  CompactHeader,
  CompactSubtitle,
  CompactTitle,
  FiltersToggle,
  Main,
  Page
} from './Forecasting.styles';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';

const LENS_LABELS: Record<KpiLens, string> = {
  dynamicForecastRevenue: 'Dynamic Forecast — Revenue',
  dynamicForecastGp: 'Dynamic Forecast — Gross Profit',
  /* The sales team is measured against Target here, not the Budgetv2 budget
     the financial year page shows. The budget* lenses hold Target on this
     page (see computeQuarterTotals). */
  budgetRevenue: 'Target — Revenue',
  budgetGp: 'Target — Gross Profit',
  vsBudgetRevenue: 'vs Target — Revenue',
  vsBudgetGp: 'vs Target — Gross Profit'
};

/* The quarter and financial year are always set, so they are not counted as
   filters someone has picked. */
const ALWAYS_SET_FILTERS = new Set(['fy', 'dateFrom', 'dateTo']);

function countPickedFilters(filters: DashboardFilters): number {
  return Object.entries(filters).filter(
    ([key, value]) => !ALWAYS_SET_FILTERS.has(key) && Array.isArray(value) && value.length > 0
  ).length;
}

const ThreeMonthForecast: FC = () => {
  const defaultFilters = useMemo(() => getDefaultThreeMonthFilters(), []);
  const [filters, setFilters] = useState<DashboardFilters>(() => defaultFilters);
  const [activeLens, setActiveLens] = useState<KpiLens>('dynamicForecastRevenue');
  const [levelIndex, setLevelIndex] = useState(0);
  const [drillSelection, setDrillSelection] = useState<DrillSelection>({});
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  /* `YYYY-MM` picked on the monthly chart. Kept apart from the date range: the
     chart still shows the whole quarter, and everything else narrows to it. */
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  /* Closed to start, so the first screen reaches the tables; the button says
     how many filters are in play while the column is hidden. */
  const [filtersOpen, setFiltersOpen] = useState(false);
  const pickedFilterCount = countPickedFilters(filters);

  /* Load every FY the date range reaches, as the full-year page does: a range
     crossing April fetched only its end month's FY, so the months before it
     showed nothing. */
  const fys = useMemo(
    () => fyCodesInRange(filters.dateFrom, filters.dateTo),
    [filters.dateFrom, filters.dateTo]
  );
  const { data: allRows = [], isFetching, isError } = useGetForecastingForFysQuery(
    { fys },
    { skip: !fys.length }
  );
  const fyOptions = useMemo(() => uniqueFyOptions(allRows), [allRows]);

  const filteredRows = useMemo(() => filterRows(allRows, filters), [allRows, filters]);
  const monthRows = useMemo(() => filterRowsByMonth(filteredRows, selectedMonth), [filteredRows, selectedMonth]);
  const totals = useMemo(() => computeQuarterTotals(monthRows), [monthRows]);
  const statusesNotCounted = uncountedStatuses(filters.status);
  const activeMetric = metricFromLens(activeLens);
  const metricLabel = activeMetric === 'revenue' ? 'Revenue' : 'Gross Profit';
  const level = useMemo(() => getDrillLevelConfig(levelIndex), [levelIndex]);

  const chartRows = useMemo(
    () => filterRowsByDrillSelection(monthRows, drillSelection, levelIndex),
    [monthRows, drillSelection, levelIndex]
  );

  // Every month of the quarter, so the selected one can be seen against the rest.
  const quarterChartRows = useMemo(
    () => filterRowsByDrillSelection(filteredRows, drillSelection, levelIndex),
    [filteredRows, drillSelection, levelIndex]
  );

  const selectionLabel = useMemo(
    () => drillSelectionLabel(drillSelection, levelIndex),
    [drillSelection, levelIndex]
  );

  const monthlyGrouped = useMemo(
    () => groupByMonthGrouped(quarterChartRows, filters.dateFrom, filters.dateTo),
    [quarterChartRows, filters.dateFrom, filters.dateTo]
  );

  const selectedMonthLabel = monthlyGrouped.find((m) => m.monthKey === selectedMonth)?.axisLabel;

  const summaryData = useMemo(
    () => groupDrillSummary(monthRows, levelIndex),
    [monthRows, levelIndex]
  );

  const comparisonData = useMemo(
    () => groupDrillComparison(chartRows, activeMetric, levelIndex),
    [chartRows, activeMetric, levelIndex]
  );

  const campaignRows = useMemo(() => buildCampaignDetailRows(chartRows), [chartRows]);

  useEffect(() => {
    setDrillSelection({});
    setSelectedRowKey(null);
  }, [filters, activeLens]);

  useEffect(() => {
    setDrillSelection({});
    setSelectedRowKey(null);
  }, [levelIndex]);

  // A new date range or filter may not contain the month that was picked.
  useEffect(() => {
    setSelectedMonth(null);
  }, [filters]);

  // Clicking the selected month again clears it, as in Looker Studio.
  const handleMonthClick = (monthKey: string) => {
    setSelectedMonth((current) => (current === monthKey ? null : monthKey));
  };

  const handleFilterChange = (patch: Partial<DashboardFilters>) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      Object.keys(patch).forEach((k) => {
        if (patch[k as keyof DashboardFilters] === undefined) {
          delete next[k as keyof DashboardFilters];
        }
      });
      return next;
    });
  };

  const handleClearAllFilters = () => {
    setFilters(defaultFilters);
  };

  const handleRowSelect = (row: BuyingPointSummaryRow) => {
    if (selectedRowKey === row.key) {
      setDrillSelection({});
      setSelectedRowKey(null);
      return;
    }

    const nextSelection: DrillSelection = {};
    for (let i = 0; i <= levelIndex; i++) {
      const drillLevel = DRILL_LEVELS[i];
      if (i < levelIndex) {
        const parent = drillSelection[drillLevel.level];
        if (parent) nextSelection[drillLevel.level] = parent;
      } else {
        nextSelection[drillLevel.level] = row.label;
      }
    }

    setDrillSelection(nextSelection);
    setSelectedRowKey(row.key);
  };

  const clearSelection = () => {
    setDrillSelection({});
    setSelectedRowKey(null);
  };

  const drillUp = () => setLevelIndex((i) => Math.max(0, i - 1));
  const drillDown = () => setLevelIndex((i) => Math.min(DRILL_LEVELS.length - 1, i + 1));

  const chartTitle = selectionLabel
    ? `${LENS_LABELS[activeLens]} · ${selectionLabel}`
    : LENS_LABELS[activeLens];

  return (
    <Page>
      <CompactHeader>
        <CompactTitle>Current Quarter Forecast</CompactTitle>
        <CompactSubtitle>
          <strong>{formatCurrentQuarterLabel()}</strong> · UK financial year quarter based on today
        </CompactSubtitle>
        <FiltersToggle
          type="button"
          $open={filtersOpen}
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((open) => !open)}
        >
          <FilterOutlined />
          {filtersOpen ? 'Hide filters' : 'Filters'}
          {pickedFilterCount > 0 && ` (${pickedFilterCount})`}
        </FiltersToggle>
      </CompactHeader>

      {isError && (
        <Alert type="error" message="Failed to load forecasting data" style={{ marginBottom: 16 }} showIcon />
      )}

      {!isFetching && !isError && allRows.length === 0 && (
        <Alert
          type="warning"
          message={`No forecasting data returned for ${formatFyLabel(filters.fy)}`}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {statusesNotCounted.length > 0 && (
        <Alert
          type="info"
          message={`${statusesNotCounted.join(', ')} ${
            statusesNotCounted.length > 1 ? "aren't" : "isn't"
          } counted in the Dynamic Forecast, Booked + Pipe or Target figures`}
          description="Those deals leave the cards and chart empty. They are listed under Pre-Pipe in the campaign detail below."
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <Spin spinning={isFetching}>
        <CollapsibleLayout $filtersOpen={filtersOpen}>
          {filtersOpen && (
            <FilterSidebar
              filters={filters}
              allRows={allRows}
              fyOptions={fyOptions}
              defaultDateRange={defaultFilters}
              onChange={handleFilterChange}
              onClearAll={handleClearAllFilters}
            />
          )}

          <Main>
            <ThreeMonthKpiScorecards totals={totals} activeLens={activeLens} onSelect={setActiveLens} />

            <ActiveLensHint>
              Showing: <strong>{LENS_LABELS[activeLens]}</strong>
              {selectionLabel && (
                <>
                  {' · '}
                  Filter: <strong>{selectionLabel}</strong>
                  {' '}
                  <button
                    type="button"
                    onClick={clearSelection}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: `rgba(${APP_SHELL_THEME.overlayRgb}, 0.55)`,
                      cursor: 'pointer',
                      fontSize: 12,
                      padding: 0,
                      textDecoration: 'underline'
                    }}
                  >
                    Clear
                  </button>
                </>
              )}
              {selectedMonthLabel && (
                <>
                  {' · '}
                  Month: <strong>{selectedMonthLabel}</strong>
                  {' '}
                  <button
                    type="button"
                    onClick={() => setSelectedMonth(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: `rgba(${APP_SHELL_THEME.overlayRgb}, 0.55)`,
                      cursor: 'pointer',
                      fontSize: 12,
                      padding: 0,
                      textDecoration: 'underline'
                    }}
                  >
                    Clear
                  </button>
                </>
              )}
              {' · '}
              Click a month on the chart or a table row to filter
            </ActiveLensHint>

            <MonthlyGroupedChart
              data={monthlyGrouped}
              title={chartTitle}
              selectedMonth={selectedMonth}
              onMonthClick={handleMonthClick}
            />

            <BuyingPointSummaryTable
              data={summaryData}
              levelTitle={level.title}
              canDrillUp={canDrillUpLevel(levelIndex)}
              canDrillDown={canDrillDownLevel(levelIndex)}
              onDrillUp={drillUp}
              onDrillDown={drillDown}
              selectedKey={selectedRowKey}
              onRowSelect={handleRowSelect}
            />

            <BuyingPointComparisonChart
              data={comparisonData}
              metricLabel={metricLabel}
              title={`${level.title} — ${metricLabel}`}
            />

            <CampaignDetailTable data={campaignRows} />
          </Main>
        </CollapsibleLayout>
      </Spin>
    </Page>
  );
};

export default ThreeMonthForecast;
